import React, { useState } from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_CONFIG } from '../../services/stripe/config';
import { supabase } from '../../lib/supabase';

interface StripePaymentButtonProps {
  amount: number;
  onSuccess: () => void;
  onError: (error: string) => void;
  disabled?: boolean;
}

export function StripePaymentButton({ amount, onSuccess, onError, disabled }: StripePaymentButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthContext();
  const stripePromise = React.useMemo(() => {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      console.error('Stripe publishable key is not configured');
      return null;
    }
    return loadStripe(key);
  }, []);

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Starting payment process...', { amount, userId: user?.id });

      // Validate stripe initialization
      if (!stripePromise) {
        throw new Error('Payment system is not properly configured');
      }

      // Validate user authentication
      if (!user) {
        throw new Error('Please sign in to continue');
      }

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error('Failed to get authentication session');
      }
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      console.log('Creating checkout session...');
      
      // Create Checkout Session
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-stripe-checkout`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          priceId: STRIPE_CONFIG.priceId,
          successUrl: STRIPE_CONFIG.successUrl,
          cancelUrl: STRIPE_CONFIG.cancelUrl
        }),
      });

      console.log('Checkout response status:', response.status);

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Raw error response:', responseText);
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { error: responseText };
        }
        console.error('Parsed error data:', errorData);
        
        throw new Error(
          errorData?.error || 
          errorData?.message || 
          `Payment setup failed (${response.status}). Please try again.`
        );
      }

      const data = await response.json();
      console.log('Checkout session response:', data);

      // Handle direct URL response
      if (data.url) {
        console.log('Redirecting to checkout URL...');
        window.location.href = data.url;
        return;
      }
      
      // Fall back to sessionId flow if URL is not provided
      if (data.sessionId) {
        console.log('Redirecting to Stripe checkout via sessionId...');
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Payment system initialization failed');
        }

        const { error: redirectError } = await stripe.redirectToCheckout({
          sessionId: data.sessionId
        });

        if (redirectError) {
          console.error('Redirect error:', redirectError);
          throw new Error(redirectError.message || 'Failed to start payment process');
        }
      } else {
        throw new Error('Invalid response from payment service: missing both url and sessionId');
      }

      onSuccess();
    } catch (err) {
      console.error('Payment error:', {
        error: err,
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined
      });
      
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'string'
          ? err
          : 'Payment failed. Please try again.';
      
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handlePayment}
        disabled={disabled || loading}
        className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-[var(--accent-color)] text-black rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {loading ? (
          <Loader2 className="animate-spin h-5 w-5" />
        ) : (
          <CreditCard className="h-5 w-5" />
        )}
        {loading ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
      </button>
      {error && (
        <div className="mt-2 text-sm text-red-500 text-center">
          {error}
        </div>
      )}
    </>
  );
}

export default StripePaymentButton